// Constantes de simulation partagées client/serveur.
// Toutes les valeurs de gameplay sont des propositions du dossier, à mesurer et ajuster.

export const TICK_RATE = 30;
export const DT = 1 / TICK_RATE;
/** Un snapshot toutes les N ticks (30 Hz / 2 = 15 Hz). */
export const SNAPSHOT_EVERY = 2;

/** Arène carrée centrée sur l'origine : coordonnées de -HALF à +HALF. */
export const ARENA_SIZE = 7200;
export const ARENA_HALF = ARENA_SIZE / 2;

/** Vitesse de base d'un tank (unités/s). */
export const BASE_SPEED = 180;
/** Réactivité du déplacement : plus grand = accélération plus vive. */
export const MOVE_RESPONSE = 7.5;

export const TANK_BASE_RADIUS = 24;

/**
 * Champ de vision équitable : la surface de monde visible est fixe, quel que soit l'écran.
 * Le ratio d'aspect est borné pour qu'un écran ultra-large ne révèle pas plus de terrain utile.
 */
// Dézoom de 15 % (choix de Léni, 4 novembre 2026 : les tanks prenaient trop de place à l'écran) : largeur et hauteur
// de la vue × 1,15, donc surface × 1,3225. Pour tout le monde, téléphone comme PC.
export const VIEW_AREA_BASE = 1300 * 731 * 1.15 * 1.15;
export const VIEW_ASPECT_MIN = 1.0;
export const VIEW_ASPECT_MAX = 2.2;
/** Marge d'envoi autour de la vue pour éviter les apparitions brutales en bord d'écran. */
export const VIEW_SEND_MARGIN = 220;

export const PARTICIPANTS_TARGET = 24;
export const HUMAN_CAP_PER_ARENA = 40;

export const SPAWN_PROTECTION_S = 5;
export const ROTATION_PROTECTION_S = 3;
export const REGEN_DELAY_S = 8;
export const CONTACT_INTERVAL_S = 0.25;
export const DEATH_SNAPSHOT_TIMEOUT_S = 30;
export const RECONNECT_GRACE_S = 30;

/** Fenêtre de contribution pour les éliminations de tanks. */
export const ASSIST_WINDOW_S = 15;

/** Niveau maximal du dossier : équipes, survie et salons privés s'y arrêtent. */
export const MAX_LEVEL = 60;
/** Arène libre publique : niveau maximal 250 (choix de Léni, 5 novembre 2026). */
export const FFA_MAX_LEVEL = 250;
/** Plus haut niveau possible dans un mode, quel qu'il soit (tables d'XP, bornes). */
export const LEVEL_CEILING = FFA_MAX_LEVEL;
/** Plafond par statistique jusqu'au niveau 60 ; il monte ensuite jusqu'à STAT_CAP_MAX (statCapForLevel). */
export const STAT_CAP = 10;
export const STAT_CAP_MAX = 20;

/** Le joueur n'est pas censé envoyer plus de ~1 input par tick ; tampon borné. */
export const INPUT_BUFFER_MAX = 4;
export const NAME_MAX_LEN = 16;

export function viewSize(aspect: number, zoom = 1): { w: number; h: number } {
  const a = Math.min(VIEW_ASPECT_MAX, Math.max(VIEW_ASPECT_MIN, Number.isFinite(aspect) ? aspect : 16 / 9));
  const area = VIEW_AREA_BASE * zoom * zoom;
  const h = Math.sqrt(area / a);
  return { w: h * a, h };
}

/**
 * Au-delà du niveau 60 (arène libre seulement), taille et champ de vision grandissent 4 fois moins vite
 * qu'avant 60 (choix de Léni, 5 novembre 2026) : taille ×1,75 et vision ×1,65 au niveau 250.
 */
const PAST_60_GROWTH = 0.25;

function growth(level: number, perLevel: number): number {
  const l = Math.max(0, Math.min(level, LEVEL_CEILING));
  return Math.min(l, MAX_LEVEL) * perLevel + Math.max(0, l - MAX_LEVEL) * perLevel * PAST_60_GROWTH;
}

/** Le champ de vision s'élargit légèrement avec la taille du tank (niveau). */
export function viewZoomForLevel(level: number): number {
  return 1 + growth(level, 0.006);
}

export function tankRadiusForLevel(level: number): number {
  return TANK_BASE_RADIUS * (1 + growth(level, 0.007));
}

/**
 * Vie et dégâts d'un tank suivent le même facteur de niveau (+4 % par niveau, ×3,36 au niveau 60, ×10,96 au 250) :
 * à niveau égal un duel dure autant au niveau 1 qu'au niveau 250, mais formes, monstres et boss tombent plus vite.
 */
export function levelPowerMul(level: number): number {
  return 1 + 0.04 * (Math.min(level, LEVEL_CEILING) - 1);
}
